import { pageSizeByDefault } from 'constants/constants';
import { FilterModel, ISort } from 'features/common/types';
import routes from 'constants/routes';
import { advancedListFilters } from '../components/ServicesDataList/constants';
import {
	EMPTY_QUICK_DATE_STATE,
	mergeQuickDateParams,
	parseServicesQuickDateState,
	QUICK_DATE_PARAM_KEYS,
	ServicesQuickDateState,
} from './servicesQuickDateFilters';

const FILTER_KEYS = new Set(advancedListFilters.map((filter) => filter.key));
const RESERVED_PARAMS = new Set(['search', 'page', 'size', 'sort', 'cols', ...QUICK_DATE_PARAM_KEYS]);

export interface ServicesListPersistedState {
	search: string;
	page: number;
	size: number;
	sortFields: ISort[];
	filterApplied: FilterModel[];
	quickDateState: ServicesQuickDateState;
	showOptionalColumns: boolean;
}

export const SERVICES_LIST_DEFAULT_SORT: ISort[] = [
	{ field: 'agencyId', order: 'asc' },
	{ field: 'created', order: 'desc' },
];

const DEFAULT_SORT_PARAM = 'agencyId,-created';

export const SERVICES_LIST_STATE_KEY = 'servicesListSearch';
const SERVICES_LIST_STORAGE_KEY = 'services-list-search';
const SERVICES_LIST_COLS_STORAGE_KEY = 'services-list-show-all-cols';

export const normalizeServicesListSearch = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

export const toServicesListSearchParam = (queryString: string): string =>
	queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : '';

export const saveServicesListSearch = (search: string): void => {
	const normalized = toServicesListSearchParam(normalizeServicesListSearch(search));
	if (normalized) {
		sessionStorage.setItem(SERVICES_LIST_STORAGE_KEY, normalized);
	} else {
		sessionStorage.removeItem(SERVICES_LIST_STORAGE_KEY);
	}
};

export const readServicesListSearch = (): string => sessionStorage.getItem(SERVICES_LIST_STORAGE_KEY) ?? '';

export const readInitialServicesListState = (): ServicesListPersistedState => {
	const urlSearch = normalizeServicesListSearch(typeof window !== 'undefined' ? window.location.search : '');
	const storedSearch = normalizeServicesListSearch(readServicesListSearch());
	return parseServicesListState(urlSearch || storedSearch);
};

export const readShowOptionalColumns = (): boolean => {
	try {
		return sessionStorage.getItem(SERVICES_LIST_COLS_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
};

export const saveShowOptionalColumns = (show: boolean): void => {
	try {
		if (show) {
			sessionStorage.setItem(SERVICES_LIST_COLS_STORAGE_KEY, 'true');
		} else {
			sessionStorage.removeItem(SERVICES_LIST_COLS_STORAGE_KEY);
		}
	} catch {
		// ignore
	}
};

const filterDefinitionByKey = new Map(advancedListFilters.map((filter) => [filter.key, { ...filter }]));

const sortToParam = (sortFields: ISort[]): string | undefined => {
	if (!sortFields.length) return undefined;
	return sortFields.map((item) => (item.order === 'desc' ? `-${item.field}` : item.field)).join(',');
};

const sortFromParam = (value: string | null): ISort[] => {
	if (!value) return SERVICES_LIST_DEFAULT_SORT;
	return value.split(',').map((part) => {
		const trimmed = part.trim();
		if (trimmed.startsWith('-')) {
			return { field: trimmed.slice(1), order: 'desc' as const };
		}
		return { field: trimmed, order: 'asc' as const };
	});
};

export const parseServicesListState = (search: string): ServicesListPersistedState => {
	const params = new URLSearchParams(normalizeServicesListSearch(search));
	const filterApplied: FilterModel[] = [];

	params.forEach((value, key) => {
		if (RESERVED_PARAMS.has(key) || !FILTER_KEYS.has(key) || !value) return;
		const definition = filterDefinitionByKey.get(key);
		if (!definition) return;
		filterApplied.push({ ...definition, value });
	});

	const colsFromUrl = params.get('cols') === 'all';
	const showOptionalColumns = colsFromUrl || readShowOptionalColumns();
	if (colsFromUrl) {
		saveShowOptionalColumns(true);
	}

	return {
		search: params.get('search') ?? '',
		page: Math.max(1, Number(params.get('page')) || 1),
		size: Math.max(1, Number(params.get('size')) || pageSizeByDefault),
		sortFields: sortFromParam(params.get('sort')),
		filterApplied,
		quickDateState: parseServicesQuickDateState(normalizeServicesListSearch(search)),
		showOptionalColumns,
	};
};

export const buildServicesListSearch = (state: {
	search: string;
	page: number;
	size: number;
	sortFields: ISort[];
	filterApplied: FilterModel[];
	quickDateState?: ServicesQuickDateState;
}): string => {
	const params = new URLSearchParams();

	if (state.search.trim()) {
		params.set('search', state.search.trim());
	}

	if (state.page > 1) {
		params.set('page', String(state.page));
	}

	if (state.size !== pageSizeByDefault) {
		params.set('size', String(state.size));
	}

	const sortParam = sortToParam(state.sortFields);
	if (sortParam && sortParam !== DEFAULT_SORT_PARAM) {
		params.set('sort', sortParam);
	}

	state.filterApplied.forEach((filter) => {
		if (filter.value === undefined || filter.value === null || filter.value === '') return;
		params.set(filter.key, String(filter.value));
	});

	mergeQuickDateParams(params, state.quickDateState ?? EMPTY_QUICK_DATE_STATE);

	return params.toString();
};

/** Compara dos query strings ignorando orden de params y `cols` legacy. */
export const areServicesListSearchesEqual = (a: string, b: string): boolean => {
	const normalize = (raw: string): string => {
		const params = new URLSearchParams(normalizeServicesListSearch(raw));
		params.delete('cols');
		const entries = [...params.entries()].sort(([ka], [kb]) => ka.localeCompare(kb));
		return new URLSearchParams(entries).toString();
	};
	return normalize(a) === normalize(b);
};

export const servicesListPathWithSearch = (search: string): string => {
	if (!search) return routes.salesOrder.serviceList;
	return search.startsWith('?')
		? `${routes.salesOrder.serviceList}${search}`
		: `${routes.salesOrder.serviceList}?${search}`;
};
